import { Frame, Note, useWidgetText } from "../shared";
import { dayMonth, localDate } from "../statistics";
import type { WidgetProps } from "../types";
import { StatisticsWidget } from "./StatisticsParts";

const TONES = ["bg-brand", "bg-brand/35"];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/**
 * year_compare: the twelve months of this year beside last year, with each year's total so far (pro-statistics §2,
 * PST-24, PST-32). Every bar has a tooltip, and each month's two values are also a list a screen reader reaches.
 */
export function YearCompare(props: WidgetProps) {
  const fmt = useWidgetText();
  const { widget, now, tz } = props;
  return (
    <StatisticsWidget
      props={props}
      body={({ answer, point, name, icon, footer, offline }) => {
        const years = (answer.months ?? []).slice(0, 2);
        const value = (y: (typeof years)[number], m: number) => y.months.find((x) => x.month === m)?.value ?? null;
        const max = Math.max(...years.flatMap((y) => y.months.map((m) => Math.abs(m.value ?? 0))), Number.EPSILON);
        const totals = years.filter((y) => y.total_so_far !== null);
        const label = `${name}: ${totals.map((y) => `${y.year} ${fmt.value(y.total_so_far ?? 0, point)}`).join(", ")}`;
        const shown = (v: number | null) => (v === null ? "—" : fmt.value(v, point));
        return (
          <Frame widget={widget} state={offline ? "offline" : "normal"} title={name} icon={icon} label={label}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700">
              {years.map((y, i) => (
                <span key={y.year} className="flex items-center gap-1">
                  <span aria-hidden="true" className={`inline-block size-2.5 rounded-sm ${TONES[i]}`} />
                  <span>{y.year}</span>
                </span>
              ))}
            </div>
            <div aria-hidden="true" className="grid min-h-16 flex-1 grid-cols-12 items-end gap-1">
              {MONTHS.map((m) => (
                <div key={m} className="flex h-full min-w-0 flex-col justify-end">
                  <div className="flex flex-1 items-end justify-center gap-px">
                    {years.map((y, i) => {
                      const v = value(y, m);
                      return (
                        <span
                          key={y.year}
                          title={`${fmt.t(`month.${m}`)} ${y.year}: ${shown(v)}`}
                          className={`w-1/2 max-w-3 rounded-t-sm ${TONES[i]}`}
                          style={{ height: `${(((v === null ? 0 : Math.abs(v)) / max) * 100).toFixed(1)}%` }}
                        />
                      );
                    })}
                  </div>
                  <span className="truncate text-center text-[0.65rem] text-gray-700">{fmt.t(`monthShort.${m}`)}</span>
                </div>
              ))}
            </div>
            <ul className="sr-only">
              {MONTHS.map((m) => (
                <li key={m}>
                  {`${fmt.t(`month.${m}`)}: ${years.map((y) => `${y.year} ${shown(value(y, m))}`).join(", ")}`}
                </li>
              ))}
            </ul>
            {/* An averaged value's year figures are means, not totals (plans-statistics-polish PSP-04). */}
            <Note>
              {fmt.t(answer.aggregate === "avg" ? "statistics.averagesTo" : "statistics.totalsTo", {
                date: dayMonth(localDate(now.getTime(), tz), fmt.t),
              })}
            </Note>
            <p className="flex flex-wrap gap-x-3 text-xs text-gray-900 tabular-nums">
              {totals.map((y) => (
                <span key={y.year}>
                  {fmt.t("statistics.yearTotal", { year: y.year, value: fmt.value(y.total_so_far ?? 0, point) })}
                </span>
              ))}
            </p>
            {footer}
          </Frame>
        );
      }}
    />
  );
}
